import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Spin from 'antd/lib/spin';
import * as React from 'react';
import { Release, ReleaseType, TimelineEvent, TimelineEventStatus, ReleaseStepType } from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { TimelineEventItem } from './TimelineEventItem';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useSelector } from 'react-redux';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectShouldHideNonBlockingSteps,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { FeatureSection } from '../../typescriptHelpers';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import { getReleaseTypeForNonBlockingSteps, isNonBlockingStepForReleaseType } from '@/helpers';
import { RELEASE_STEP_TYPE_BY_EVENT_TYPE } from '@/components/timelineEvents/timelineEventHelpers';
import Flex from 'antd/lib/flex';

export enum ReleaseTimelineSectionContext {
    ReleaseStep = 'releaseStep',
    AppHome = 'appHome',
}

interface EventsTimelineProps {
    events: TimelineEvent[];
    maxItemsToDisplay?: number;
    isLoading?: boolean;
    release?: Release;
    orgId: string;
    appId: string;
    shouldHideUpcomingEvents?: boolean;
    releaseType: ReleaseType;
    releaseStepType?: ReleaseStepType;
    context: ReleaseTimelineSectionContext;
}

export const EventsTimeline: React.FC<EventsTimelineProps> = ({
    events,
    maxItemsToDisplay,
    orgId,
    appId,
    isLoading,
    shouldHideUpcomingEvents,
    release,
    releaseType,
    releaseStepType,
    context,
}) => {
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appId),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const filteredEvents = events?.filter((event) => {
        let areUpcomingEventsHidden = true;
        if (shouldHideUpcomingEvents) {
            areUpcomingEventsHidden = event.status !== TimelineEventStatus.Upcoming;
        }

        let isReleaseStepNotHidden = true;
        if (shouldHideNonBlockingSteps) {
            const releaseStepTypeForEvent = RELEASE_STEP_TYPE_BY_EVENT_TYPE[event.type];
            isReleaseStepNotHidden = !isNonBlockingStepForReleaseType(
                nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(releaseType)],
                releaseStepTypeForEvent,
            );
        }

        return areUpcomingEventsHidden && isReleaseStepNotHidden;
    });

    const firstItems = maxItemsToDisplay ? filteredEvents?.slice(0, maxItemsToDisplay) : filteredEvents;

    const upcomingEvents = firstItems?.filter(
        (event) => event.status === TimelineEventStatus.Upcoming || event.status === TimelineEventStatus.Target,
    );
    const allOtherEvents = firstItems?.filter(
        (event) => event.status !== TimelineEventStatus.Upcoming && event.status !== TimelineEventStatus.Target,
    );

    if (isLoading) {
        return (
            <Space>
                <Spin spinning />
                <Typography.Text style={{ color: runwayColors.primary, fontWeight: 600, paddingLeft: 6 }}>
                    Loading timeline...
                </Typography.Text>
            </Space>
        );
    }

    if (events.length === 0) {
        return <></>;
    }

    return (
        <Flex
            align="flex-start"
            gap={6}
            style={{
                backgroundColor: releaseStepType ? antGrey.grey2 : null,
                padding: 10,
                maxWidth: releaseStepType ? '600px' : null,
            }}
        >
            <EmptyStateWrapper
                appId={appId}
                featureSections={[FeatureSection.HistoryTimeline]}
                releaseId={release?.id}
                placement="left"
                shouldHideDotOnHover={false}
            />
            <Flex
                vertical
                justify="center"
                style={{
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {!shouldHideUpcomingEvents && upcomingEvents?.length > 0 && (
                    <>
                        <TransitionGroup
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {upcomingEvents.map((timelineEvent) => {
                                const key = timelineEvent.triggeredAt + timelineEvent.type;

                                return (
                                    <CSSTransition key={key} timeout={500} classNames="timeline-event">
                                        <TimelineEventItem
                                            timelineEvent={timelineEvent}
                                            orgId={orgId}
                                            appId={appId}
                                            appPlatform={appPlatform}
                                            appStoreType={appStoreType}
                                            releaseStepType={releaseStepType}
                                            context={context}
                                        />
                                    </CSSTransition>
                                );
                            })}
                        </TransitionGroup>
                        <Divider
                            style={{
                                margin: '16px 0',
                            }}
                        />
                    </>
                )}
                <TransitionGroup
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {allOtherEvents.map((timelineEvent) => {
                        const key = timelineEvent.triggeredAt + timelineEvent.type;

                        return (
                            <CSSTransition key={key} timeout={500} classNames="timeline-event">
                                <TimelineEventItem
                                    timelineEvent={timelineEvent}
                                    orgId={orgId}
                                    appId={appId}
                                    appPlatform={appPlatform}
                                    appStoreType={appStoreType}
                                    releaseStepType={releaseStepType}
                                    context={context}
                                    shouldHideEventCount
                                />
                            </CSSTransition>
                        );
                    })}
                </TransitionGroup>
            </Flex>
        </Flex>
    );
};
